import { TimelineLite } from "gsap";
import * as HLF from "html-living-framework";
import { Application } from "../Application";
import { CaptionToken } from "../Data/CaptionToken";
import * as Topics from "../Data/Topics";
import * as VoiceOver from "../Data/VoiceOver";

enum SpineTrack {
    BODY,
    EYES,
    MOUTH,
    NUM_ELEMENTS,
}

interface IAnimData {
    animKey: string[];
    vo: string[];
    brushVersion?: IAnimData;
}

export class Andy extends PIXI.Container {
    private static readonly ANIM_AMAZED1: string = "amazed1";
    private static readonly ANIM_AMAZED1B: string = "amazed1b";
    private static readonly ANIM_AMAZED2: string = "amazed2";
    private static readonly ANIM_BLINK: string = "blink";
    private static readonly ANIM_IDLE: string = "idle";
    private static readonly ANIM_GESTURE_TO_MIDDLE: string = "gesture_to_middle";
    private static readonly ANIM_FINGERS: string = "react_fingers";
    private static readonly ANIM_WOW: string = "react_wow";
    private static readonly ANIM_TAP_FACE: string = "tap_face_with_finger";
    private static readonly ANIM_TAP_FACE_WITH_BRUSH: string = "tap_face_with_brush";
    private static readonly ANIM_ONBOARDING: string = "onboarding_point";
    private static readonly ANIM_WAVE: string = "wave_at_user";

    private static readonly AMAZED1_BRUSH: IAnimData = {
        animKey: [ Andy.ANIM_AMAZED1 ],
        vo: [ VoiceOver.NICE, VoiceOver.EXCELLENT ],
    };
    private static readonly AMAZED1: IAnimData = {
        animKey: [ Andy.ANIM_AMAZED1B ],
        vo: [ VoiceOver.NICE, VoiceOver.EXCELLENT ],
        brushVersion: Andy.AMAZED1_BRUSH,
    };
    private static readonly AMAZED2: IAnimData = {
        animKey: [ Andy.ANIM_AMAZED2 ],
        vo: [ VoiceOver.INCREDIBLE, VoiceOver.AWESOME ],
    };
    private static readonly WOW: IAnimData = {
        animKey: [ Andy.ANIM_WOW ],
        vo: [ VoiceOver.WHOA, VoiceOver.WOW ],
    };
    private static readonly FINGERS: IAnimData = {
        animKey: [ Andy.ANIM_FINGERS ],
        vo: [ VoiceOver.OH, VoiceOver.THATS_AMAZING ],
    };
    private static readonly ONBOARDING_POINT: IAnimData = {
        animKey: [ Andy.ANIM_ONBOARDING ],
        vo: [ VoiceOver.ONBOARDING_00, VoiceOver.ONBOARDING_01, VoiceOver.ONBOARDING_02 ],
    };
    private static readonly WAVE: IAnimData = {
        animKey: [ Andy.ANIM_WAVE ],
        vo: [ VoiceOver.HELLO, VoiceOver.HI, VoiceOver.HEY ],
    };
    private static readonly TAP_FACE_BRUSH: IAnimData = {
        animKey: [ Andy.ANIM_TAP_FACE_WITH_BRUSH ],
        vo: [ VoiceOver.HM_OOPS, VoiceOver.HM_UHOH, VoiceOver.HM_WOOPS ],
    };
    private static readonly TAP_FACE: IAnimData = {
        animKey: [ Andy.ANIM_TAP_FACE ],
        vo: [ VoiceOver.HMM, VoiceOver.MHM, VoiceOver.INTERESTING ],
        brushVersion: Andy.TAP_FACE_BRUSH,
    };

    private static readonly GALLERY_REACTIONS: IAnimData[] = [ Andy.AMAZED1, Andy.AMAZED2 ];
    private static readonly GAMEPLAY_REACTIONS: IAnimData[] = [ Andy.WOW, Andy.FINGERS ];

    private _spine: PIXI.spine.Spine;
    private _blinkTimer!: TimelineLite;
    private _currentHappyAnimTimeLeft: number;
    private _prevPlayedHappyAnim: string;
    private _interactionIdleTimer!: TimelineLite;
    private _messageToken: any;
    private _isOnboardingAnimOnCooldown: boolean;
    private _isReactionAnimOnCooldown: boolean;
    private _isPaintingGame: boolean;
    private _isMenu: boolean;
    private _prevAnimData: IAnimData | undefined;
    private _prevAnimId: string | undefined;
    private _prevVo: string | undefined;
    private _canPlayVo: boolean;
    private _playedFunFact: boolean;
    private _isOnboarding: boolean;
    private _isIdling: boolean;

    constructor() {
        super();
        this._isOnboardingAnimOnCooldown = false;
        this._isReactionAnimOnCooldown = false;
        this._isPaintingGame = false;
        this._isMenu = false;
        this._canPlayVo = true;
        this._isOnboarding = false;
        this._playedFunFact = false;
        this._isIdling = false;

        this._spine = HLF.PixiUtils.makeSpine("andy");
        this._spine.scale.set(0.35);
        this.addChild(this._spine);

        this._spine.state.addListener({
            complete: this.onAnimComplete.bind(this),
        });

        this._currentHappyAnimTimeLeft = 0;
        this._prevPlayedHappyAnim = "";

        this.playAnim(Andy.ANIM_IDLE, SpineTrack.BODY, true);
        this.queueBlink();

        this._messageToken = PubSub.subscribe(Topics.ANDY, this.onMessagePublished.bind(this));
    }

    public update(pDeltaTime: number): void {
        if (this._spine !== undefined) {
            this._spine.update(pDeltaTime);

            if (this._currentHappyAnimTimeLeft > 0) {
                this._currentHappyAnimTimeLeft -= pDeltaTime;
            }
        }
    }

    public destroy(pOptions?: boolean | PIXI.DestroyOptions): void {
        PubSub.unsubscribe(this._messageToken);
        if (this._blinkTimer !== undefined) {
            this._blinkTimer.kill();
        }
        if (this._interactionIdleTimer !== undefined) {
            this._interactionIdleTimer.kill();
        }
        this._spine.state.listeners = [];
        super.destroy(pOptions);
    }

    public cancelIdleTimer(): void {
        if (this._interactionIdleTimer !== undefined) {
            this._interactionIdleTimer.kill();
        }
    }

    private playAnim(pName: string, pTrack: number = SpineTrack.BODY, pLoop: boolean = false,
                     pResetSkeleton: boolean = false): number {
        if (this._blinkTimer !== undefined) {
            this._blinkTimer.kill();
        }
        if (pResetSkeleton) {
            this._spine.skeleton.setToSetupPose();
            this._spine.state.tracks = [];
        }
        this._spine.state.setAnimation(pTrack, pName, pLoop);
        this._spine.update(0);
        return this._spine.state.getCurrent(pTrack).animationEnd;
    }

    private playVO(pId: string | string[]): void {
        if (this._canPlayVo) {
            const id: string = (typeof pId === "string") ? pId : HLF.ArrayUtils.random(pId);
            Application.instance.audio.preload(id, HLF.AudioCategory.VO, () => {
                Application.instance.audio.play(id, 1, false, HLF.AudioCategory.VO);
                PubSub.publishSync(Topics.PLAY_CAPTION, new CaptionToken(id));
            });
        }
    }

    private queueBlink(): void {
        this._blinkTimer = new TimelineLite();
        this._blinkTimer.call(this.blink, undefined, this, HLF.Random.floatBetween(3, 8));
    }

    private blink(): void {
        this.playAnim(Andy.ANIM_BLINK, SpineTrack.EYES);
    }

    private queueInteractionIdle(): void {
        if (this._interactionIdleTimer === undefined || this._interactionIdleTimer.isActive() === false) {
            const time = HLF.Random.floatBetween(8, 12);
            this._interactionIdleTimer = new TimelineLite();
            this._interactionIdleTimer.call(this.interactionIdle.bind(this), undefined, this, time);
        }
    }

    private interactionIdle(pForceFunFact: boolean = false): void {
        const playFunFact: boolean = pForceFunFact || (this._playedFunFact === false && Math.random() < 0.75);
        if (playFunFact) {
            PubSub.publishSync(Topics.PLAY_FUN_FACT, undefined);
        }
        // Play idle animation
        else {
            this._isIdling = true;
            const anim: IAnimData = this._isPaintingGame ? Andy.TAP_FACE_BRUSH : Andy.TAP_FACE;
            this.playAnim(HLF.ArrayUtils.random(anim.animKey), SpineTrack.BODY, false, true);
            if (anim === Andy.TAP_FACE_BRUSH) {
                new TimelineLite().call(() => {
                    this.playVO(anim.vo);
                }, undefined, this, 1.75);
            }
            else {
                this.playVO(anim.vo);
            }
        }
        this._playedFunFact = playFunFact;
    }

    private onAnimComplete(pTrack: PIXI.spine.core.TrackEntry): void {
        switch (pTrack.animation.name) {
            case Andy.ANIM_TAP_FACE:
            case Andy.ANIM_TAP_FACE_WITH_BRUSH:
                this._isIdling = false;
                this.queueInteractionIdle();
                this.queueBlink();
                break;
            case Andy.ANIM_WAVE:
                if (this._isOnboarding === false) {
                    new TimelineLite().call(() => {
                        this.interactionIdle(true);
                    }, undefined, this, 2.0);
                }
                break;
            default:
                this.playAnim(Andy.ANIM_IDLE, SpineTrack.BODY, true, true);
                this.queueBlink();
                break;
        }
    }

    private onMessagePublished(pTopic: string, pData?: any): void {
        // TODO:SH: Re-add animation repeat prevention
        let anim: IAnimData | undefined;
        switch (pTopic) {
            case Topics.GALLERY_ENTERED:
                this._isMenu = true;
                break;
            case Topics.PAINTABLE_GAME_ENTERED:
                this._isPaintingGame = true;
                break;
            case Topics.GAME_ENTERED:
                anim = Andy.WAVE;
                break;
            case Topics.GALLERY_ITEM_FINISHED:
                anim = HLF.ArrayUtils.random(Andy.GALLERY_REACTIONS);
                break;
            case Topics.PAINTABLE_LAYER_PAINTED:
            case Topics.PATCH_ADDED:
            case Topics.HIEROGLYPH_CHANGED:
                if (this._isMenu === false && this._isReactionAnimOnCooldown === false && this._isIdling === false) {
                    this._isReactionAnimOnCooldown = true;
                    new TimelineLite().call(() => {
                        this._isReactionAnimOnCooldown = false;
                    }, undefined, this, 6.0);
                    do {
                        anim = HLF.ArrayUtils.random(Andy.GAMEPLAY_REACTIONS);
                    }
                    while (anim === this._prevAnimData);
                }
                break;
            case Topics.ONBOARDING_ARROW_SHOW:
                const skipAndyReaction = pData === true;
                if (!skipAndyReaction && this._isOnboardingAnimOnCooldown === false) {
                    this._isOnboardingAnimOnCooldown = true;
                    new TimelineLite().call(() => {
                        this._isOnboardingAnimOnCooldown = false;
                    }, undefined, this, 6.0);
                    anim = Andy.ONBOARDING_POINT;
                }
                break;
            case Topics.FUN_FACT_START:
                this._canPlayVo = false;
                break;
            case Topics.FUN_FACT_COMPLETE:
                this._canPlayVo = true;
                this.queueInteractionIdle();
                break;
            case Topics.ONBOARDING_START:
                this._isOnboarding = true;
                break;
            case Topics.ONBOARDING_FINISH:
                this._isOnboarding = false;
                this._interactionIdleTimer = new TimelineLite().call(() => {
                    this.interactionIdle(true);
                }, undefined, this, 2.0);
                break;
        }
        if (anim !== undefined) {
            this._prevAnimData = anim;

            let animId: string;
            do {
                animId = HLF.ArrayUtils.random(anim.animKey);
            }
            while (anim.animKey.length > 1 && animId === this._prevAnimId);
            this._prevAnimId = animId;

            let vo: string;
            do {
                vo = HLF.ArrayUtils.random(anim.vo);
            }
            while (anim.vo.length > 1 && vo === this._prevVo);
            this._prevVo = vo;

            this.playAnim(animId, SpineTrack.BODY, false, true);
            this.playVO(vo);
        }
    }
}
